import {useMemo} from "react";
import {objectPictureAssocsSelector, useGetAllObjectPictureAssocsByObjectQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {ImageCarousel, ImageCarouselProps} from "../ImageCarousel/ImageCarousel";
import {Box, Button, Skeleton} from "@mantine/core";
import {Empty} from "../../empty/Empty/Empty";
import {useTranslation} from "react-i18next";
import {HasAccess} from "@ots-frontend/ots-ui";

export type EntityImageCarouselProps = Omit<ImageCarouselProps, 'srcs'> & {
  objectId: string
  onAddClick?: () => void
  showEmpty?: boolean
}

export const EntityImageCarousel = (props: EntityImageCarouselProps) => {
  const {objectId, onAddClick, showEmpty = true, ...rest} = props

  const {t} = useTranslation(["common"])

  const selectObjectPictureAssocs = useMemo(() => {
    return objectPictureAssocsSelector()
  }, []);

  const {pictures, isLoading} = useGetAllObjectPictureAssocsByObjectQuery(objectId ?? skipToken, {
    selectFromResult: (result) => ({
      pictures: selectObjectPictureAssocs(result),
      isLoading: result.isLoading,
    })
  });

  const data = useMemo(() => {
    return pictures.map((p) => ({
      src: p.url,
      caption: t(p.supplementalType.displayValue.id, p.supplementalType.displayValue.defaultMessage) + (p.description ? " - " + p.description : "")
    }))
  }, [pictures,t])

  return (
    <Box>
      {isLoading &&
        <>
          <Skeleton height={50} circle mb="xl"/>
          <Skeleton height={8} radius="xl"/>
          <Skeleton height={8} mt={6} radius="xl"/>
          <Skeleton height={8} mt={6} width="70%" radius="xl"/>
        </>
      }
      {(showEmpty && data.length === 0) &&
        <Empty
          maw={400}
          h={100}
          mx="auto"
          message={"No Images"}
          action={
            <HasAccess permissions={["edit"]}>
              {onAddClick &&
                <Button onClick={() => onAddClick()}>
                  {t("common.action.add.image", "Add Image")}
                </Button>
              }
            </HasAccess>
          }/>
      }
      {data.length > 0 &&
        <ImageCarousel
          {...rest}
          data={data}/>
      }
    </Box>
  )
}
